/*
 * Keyboard navigation for SweetAlert2 confirm popups.
 *
 * These popups usually open on top of an MUI Modal (batch popup / search modal),
 * whose focus trap pulls focus back into the modal — so native Tab / Enter never
 * reach the Swal buttons. We intercept keys at the window capture phase (before
 * React / MUI handlers) and drive the selection ourselves:
 *   Tab / ArrowRight        → next button
 *   Shift+Tab / ArrowLeft   → previous button
 *   Enter                   → click the selected button
 *   Escape                  → click Cancel (if present)
 */

const HIGHLIGHT_OUTLINE = "3px solid rgba(42, 113, 255, 0.45)";

const getVisibleButtons = (popup) =>
  Array.from(popup.querySelectorAll(".swal2-actions button")).filter(
    (btn) => btn.style.display !== "none" && btn.offsetParent !== null
  );

const attachSwalKeyboardNav = (popup) => {
  const buttons = getVisibleButtons(popup);
  if (buttons.length === 0) return () => {};

  const confirmIndex = buttons.findIndex((btn) => btn.classList.contains("swal2-confirm"));
  let selected = confirmIndex >= 0 ? confirmIndex : 0;

  const highlight = () => {
    buttons.forEach((btn, i) => {
      btn.style.outline = i === selected ? HIGHLIGHT_OUTLINE : "none";
      btn.style.outlineOffset = i === selected ? "2px" : "";
    });
    buttons[selected]?.focus({ preventScroll: true });
  };

  const stop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  const onKeyDown = (event) => {
    const { key } = event;
    if (key === "Tab" || key === "ArrowRight" || key === "ArrowLeft") {
      stop(event);
      const backward = key === "ArrowLeft" || (key === "Tab" && event.shiftKey);
      selected = (selected + (backward ? -1 : 1) + buttons.length) % buttons.length;
      highlight();
    } else if (key === "Enter" || key === "NumpadEnter") {
      stop(event);
      /* Ignore auto-repeat so a held Enter from the previous screen can't confirm */
      if (event.repeat) return;
      buttons[selected]?.click();
    } else if (key === "Escape") {
      const cancel = popup.querySelector(".swal2-cancel");
      if (cancel && cancel.style.display !== "none") {
        stop(event);
        cancel.click();
      }
    }
  };

  /* Keep the highlight in sync if the user hovers / clicks with the mouse */
  const onMouseEnter = (event) => {
    const idx = buttons.indexOf(event.currentTarget);
    if (idx >= 0) {
      selected = idx;
      highlight();
    }
  };

  highlight();
  window.addEventListener("keydown", onKeyDown, true);
  buttons.forEach((btn) => btn.addEventListener("mouseenter", onMouseEnter));

  return () => {
    window.removeEventListener("keydown", onKeyDown, true);
    buttons.forEach((btn) => btn.removeEventListener("mouseenter", onMouseEnter));
  };
};

/**
 * Wrap Swal.fire options to add Tab / Arrow / Enter / Escape handling.
 * Existing didOpen / willClose callbacks are preserved.
 */
export const withSwalKeyboardNav = (options = {}) => {
  let cleanup = null;
  return {
    ...options,
    didOpen: (popup) => {
      options.didOpen?.(popup);
      cleanup = attachSwalKeyboardNav(popup);
    },
    willClose: (popup) => {
      cleanup?.();
      cleanup = null;
      options.willClose?.(popup);
    },
  };
};
